import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, ChevronDownIcon, EyeIcon, SendIcon, PlayIcon, CheckCircleIcon, XCircleIcon, XIcon, ClockIcon, CpuIcon, FileIcon, MailIcon, ArrowRightIcon, StopCircleIcon } from 'lucide-react';
import { StatusBadge } from '../components/ui/StatusBadge';
import { ModalDialog } from '../components/ui/ModalDialog';
import { MachineStatusIndicator } from '../components/ui/MachineStatusIndicator';
import { useIsMobile } from '../hooks/useMediaQuery';
import { motion, AnimatePresence } from 'framer-motion';
import { useRole } from '../hooks/useRole';
// --- Types ---
interface Booking {
  id: string;
  patient: string;
  email: string;
  type: string;
  examCategory: string;
  doctor: string;
  date: string;
  time: string;
  status: string;
}
// --- Machine compatibility map ---
const machineCompatibility: Record<string, string[]> = {
  'Risonanza Magnetica': ['Siemens Magnetom', 'Hitachi Oasis'],
  TAC: ['GE Revolution'],
  Ecografia: ['Philips Affiniti'],
  Radiografia: ['Carestream DRX'],
  Mammografia: ['Hologic Selenia']
};
const allMachines = [{
  id: 1,
  name: 'Siemens Magnetom',
  type: 'Risonanza Magnetica',
  status: 'Connesso' as const,
  lastActivity: '3 minuti fa',
  estimatedTime: '25 min'
}, {
  id: 2,
  name: 'GE Revolution',
  type: 'TAC',
  status: 'Connesso' as const,
  lastActivity: '8 minuti fa',
  estimatedTime: '15 min'
}, {
  id: 3,
  name: 'Philips Affiniti',
  type: 'Ecografia',
  status: 'Connesso' as const,
  lastActivity: '12 minuti fa',
  estimatedTime: '20 min'
}, {
  id: 4,
  name: 'Carestream DRX',
  type: 'Radiografia',
  status: 'Manutenzione' as const,
  lastActivity: '2 ore fa',
  estimatedTime: '10 min'
}, {
  id: 5,
  name: 'Hologic Selenia',
  type: 'Mammografia',
  status: 'Connesso' as const,
  lastActivity: '1 minuto fa',
  estimatedTime: '15 min'
}, {
  id: 6,
  name: 'Hitachi Oasis',
  type: 'Risonanza Magnetica Aperta',
  status: 'Disconnesso' as const,
  lastActivity: '1 giorno fa',
  estimatedTime: '30 min'
}];
// Today's date for comparison
const TODAY = '2026-03-11';
const mockBookings: Booking[] = [
// Past dates
{
  id: 'BKG-0990',
  patient: 'Marco Rossi',
  email: 'marco.rossi@email.it',
  type: 'Risonanza magnetica encefalo',
  examCategory: 'Risonanza Magnetica',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-08',
  time: '09:00',
  status: 'Inviato'
}, {
  id: 'BKG-0991',
  patient: 'Anna Ferrari',
  email: 'anna.ferrari@email.it',
  type: 'TAC torace',
  examCategory: 'TAC',
  doctor: 'Dr. A. Neri',
  date: '2026-03-08',
  time: '10:30',
  status: 'Da refertare'
}, {
  id: 'BKG-0992',
  patient: 'Giuseppe Russo',
  email: 'giuseppe.russo@email.it',
  type: 'Ecografia addome',
  examCategory: 'Ecografia',
  doctor: 'Dr. M. Gialli',
  date: '2026-03-09',
  time: '08:30',
  status: 'Inviato'
}, {
  id: 'BKG-0993',
  patient: 'Maria Conti',
  email: 'maria.conti@email.it',
  type: 'Radiografia colonna vertebrale',
  examCategory: 'Radiografia',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-09',
  time: '11:00',
  status: 'Da refertare'
}, {
  id: 'BKG-0994',
  patient: 'Luigi Gallo',
  email: 'luigi.gallo@email.it',
  type: 'Risonanza magnetica ginocchio',
  examCategory: 'Risonanza Magnetica',
  doctor: 'Dr. A. Neri',
  date: '2026-03-10',
  time: '09:00',
  status: 'Refertato'
}, {
  id: 'BKG-0995',
  patient: 'Francesca Moretti',
  email: 'f.moretti@email.it',
  type: 'TAC addome',
  examCategory: 'TAC',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-10',
  time: '14:00',
  status: 'Da refertare'
},
// Today
{
  id: 'BKG-1001',
  patient: 'Sofia Marino',
  email: 'sofia.marino@email.it',
  type: 'TAC pelvi',
  examCategory: 'TAC',
  doctor: 'Dr. M. Gialli',
  date: '2026-03-11',
  time: '08:00',
  status: 'In corso'
}, {
  id: 'BKG-1002',
  patient: 'Lorenzo Costa',
  email: 'lorenzo.costa@email.it',
  type: 'Risonanza magnetica encefalo',
  examCategory: 'Risonanza Magnetica',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-11',
  time: '09:30',
  status: 'In attesa'
}, {
  id: 'BKG-1003',
  patient: 'Beatrice Fontana',
  email: 'b.fontana@email.it',
  type: 'Ecografia addome',
  examCategory: 'Ecografia',
  doctor: 'Dr. A. Neri',
  date: '2026-03-11',
  time: '10:00',
  status: 'In attesa'
}, {
  id: 'BKG-1004',
  patient: 'Gabriele Leone',
  email: 'g.leone@email.it',
  type: 'Radiografia torace',
  examCategory: 'Radiografia',
  doctor: 'Dr. M. Gialli',
  date: '2026-03-11',
  time: '11:15',
  status: 'In attesa'
}, {
  id: 'BKG-1005',
  patient: 'Chiara Rizzo',
  email: 'c.rizzo@email.it',
  type: 'Mammografia bilaterale',
  examCategory: 'Mammografia',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-11',
  time: '14:00',
  status: 'In attesa'
},
// Future
{
  id: 'BKG-1006',
  patient: 'Valentina Greco',
  email: 'v.greco@email.it',
  type: 'Risonanza magnetica colonna',
  examCategory: 'Risonanza Magnetica',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-12',
  time: '09:00',
  status: 'Programmato'
}, {
  id: 'BKG-1007',
  patient: 'Riccardo Santoro',
  email: 'r.santoro@email.it',
  type: 'TAC addome',
  examCategory: 'TAC',
  doctor: 'Dr. A. Neri',
  date: '2026-03-12',
  time: '10:30',
  status: 'Programmato'
}, {
  id: 'BKG-1008',
  patient: 'Elena Colombo',
  email: 'e.colombo@email.it',
  type: 'Ecografia pelvi',
  examCategory: 'Ecografia',
  doctor: 'Dr. M. Gialli',
  date: '2026-03-13',
  time: '08:00',
  status: 'Programmato'
}, {
  id: 'BKG-1009',
  patient: 'Davide Fontana',
  email: 'd.fontana@email.it',
  type: 'Radiografia colonna vertebrale',
  examCategory: 'Radiografia',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-13',
  time: '11:00',
  status: 'Programmato'
},
// Additional bookings for Dr. S. Verdi — realistic operational data
{
  id: 'BKG-0985',
  patient: 'Alberto Mancini',
  email: 'a.mancini@email.it',
  type: 'TAC torace',
  examCategory: 'TAC',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-08',
  time: '11:30',
  status: 'Refertato'
}, {
  id: 'BKG-0986',
  patient: 'Paola Gentile',
  email: 'p.gentile@email.it',
  type: 'Ecografia addome',
  examCategory: 'Ecografia',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-08',
  time: '14:00',
  status: 'Inviato'
}, {
  id: 'BKG-0987',
  patient: 'Stefano Pellegrini',
  email: 's.pellegrini@email.it',
  type: 'Risonanza magnetica ginocchio',
  examCategory: 'Risonanza Magnetica',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-09',
  time: '08:00',
  status: 'Refertato'
}, {
  id: 'BKG-0988',
  patient: 'Lucia Ferri',
  email: 'l.ferri@email.it',
  type: 'Ecografia pelvi',
  examCategory: 'Ecografia',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-09',
  time: '14:30',
  status: 'Da refertare'
}, {
  id: 'BKG-0989',
  patient: 'Matteo Gatti',
  email: 'm.gatti@email.it',
  type: 'Radiografia torace',
  examCategory: 'Radiografia',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-10',
  time: '08:30',
  status: 'Refertato'
}, {
  id: 'BKG-0996',
  patient: 'Simona Vitale',
  email: 's.vitale@email.it',
  type: 'Risonanza magnetica encefalo',
  examCategory: 'Risonanza Magnetica',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-10',
  time: '16:00',
  status: 'Inviato'
}, {
  id: 'BKG-1010',
  patient: 'Roberto Marchetti',
  email: 'r.marchetti@email.it',
  type: 'Ecografia addome',
  examCategory: 'Ecografia',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-11',
  time: '11:00',
  status: 'In attesa'
}, {
  id: 'BKG-1011',
  patient: 'Giuliana Parisi',
  email: 'g.parisi@email.it',
  type: 'TAC addome',
  examCategory: 'TAC',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-11',
  time: '15:30',
  status: 'In attesa'
}, {
  id: 'BKG-1012',
  patient: 'Fabio Caruso',
  email: 'f.caruso@email.it',
  type: 'TAC torace',
  examCategory: 'TAC',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-12',
  time: '11:00',
  status: 'Programmato'
}, {
  id: 'BKG-1013',
  patient: 'Marta Rinaldi',
  email: 'm.rinaldi@email.it',
  type: 'Ecografia pelvi',
  examCategory: 'Ecografia',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-12',
  time: '14:30',
  status: 'Programmato'
}, {
  id: 'BKG-1014',
  patient: 'Luca De Luca',
  email: 'l.deluca@email.it',
  type: 'Risonanza magnetica colonna',
  examCategory: 'Risonanza Magnetica',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-13',
  time: '09:00',
  status: 'Programmato'
}, {
  id: 'BKG-1015',
  patient: 'Alessia Fabbri',
  email: 'a.fabbri@email.it',
  type: 'Radiografia colonna vertebrale',
  examCategory: 'Radiografia',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-13',
  time: '14:00',
  status: 'Programmato'
}, {
  id: 'BKG-1016',
  patient: 'Giorgio Conti',
  email: 'g.conti@email.it',
  type: 'Risonanza magnetica encefalo',
  examCategory: 'Risonanza Magnetica',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-14',
  time: '08:30',
  status: 'Programmato'
}, {
  id: 'BKG-1017',
  patient: 'Teresa Moretti',
  email: 't.moretti@email.it',
  type: 'Ecografia addome',
  examCategory: 'Ecografia',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-14',
  time: '10:00',
  status: 'Programmato'
}, {
  id: 'BKG-1018',
  patient: 'Nicola Barbieri',
  email: 'n.barbieri@email.it',
  type: 'TAC addome',
  examCategory: 'TAC',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-14',
  time: '14:30',
  status: 'Programmato'
}];
const uniqueDoctors = Array.from(new Set(mockBookings.map(b => b.doctor))).sort();
function getDateCategory(date: string): 'past' | 'today' | 'future' {
  if (date < TODAY) return 'past';
  if (date === TODAY) return 'today';
  return 'future';
}
function getStatusOptionsForDate(dateCategory: 'past' | 'today' | 'future'): string[] {
  switch (dateCategory) {
    case 'past':
      return ['Da refertare', 'Refertato', 'Inviato'];
    case 'today':
      return ['In attesa', 'In corso', 'Da refertare'];
    case 'future':
      return ['Programmato'];
  }
}
// Truncated cell with fixed-position tooltip (disabled on mobile)
function TruncatedCell({
  text
}: {
  text: string;
}) {
  const [show, setShow] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const isMobileView = useIsMobile();
  const handleMouseEnter = () => {
    if (isMobileView) return;
    if (textRef.current) {
      if (textRef.current.scrollWidth > textRef.current.clientWidth) {
        setShow(true);
      }
    }
  };
  return <div className="relative min-w-0" onMouseEnter={handleMouseEnter} onMouseLeave={() => setShow(false)} data-id="element-259">
      <span ref={textRef} className="block truncate" data-id="element-260">
        {text}
      </span>
      {show && !isMobileView && <div className="fixed px-2.5 py-1.5 bg-text-primary text-white text-xs rounded-md whitespace-nowrap pointer-events-none shadow-lg" style={{
      zIndex: 9999,
      transform: 'translateY(-100%) translateY(-6px)'
    }} ref={el => {
      if (el) {
        const parent = el.parentElement?.getBoundingClientRect();
        if (parent) {
          el.style.top = `${parent.top}px`;
          el.style.left = `${parent.left}px`;
        }
      }
    }} data-id="element-261">
          {text}
        </div>}
    </div>;
}
export function Bookings() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const {
    role
  } = useRole();
  const CURRENT_USER = role === 'medico' ? 'Dr. S. Verdi' : 'Superadmin';
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState(TODAY);
  const [statusFilter, setStatusFilter] = useState('Tutti');
  const [doctorFilter, setDoctorFilter] = useState('Tutti');
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  // Modals
  const [machineModalBooking, setMachineModalBooking] = useState<Booking | null>(null);
  const [selectedMachineId, setSelectedMachineId] = useState<number | null>(null);
  const [detailModalBooking, setDetailModalBooking] = useState<Booking | null>(null);
  const [resendModalBooking, setResendModalBooking] = useState<Booking | null>(null);
  const [completeExamModal, setCompleteExamModal] = useState<Booking | null>(null);
  // Toast
  const [toastMessage, setToastMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  // Bookings state (for status updates)
  const [bookings, setBookings] = useState(mockBookings);
  const dashboardBookings = useMemo(() => {
    if (role === 'medico') {
      return bookings.filter(b => b.doctor === CURRENT_USER);
    }
    return bookings;
  }, [role, CURRENT_USER, bookings]);
  // Date category for current filter
  const dateCategory = getDateCategory(dateFilter);
  const statusOptions = getStatusOptionsForDate(dateCategory);
  // Reset status filter when date changes
  useEffect(() => {
    setStatusFilter('Tutti');
    setCurrentPage(1);
  }, [dateFilter]);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, doctorFilter]);
  // Filtered data
  const filteredBookings = useMemo(() => {
    let result = dashboardBookings.filter(b => b.date === dateFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(b => b.id.toLowerCase().includes(q) || b.patient.toLowerCase().includes(q) || b.type.toLowerCase().includes(q) || b.doctor.toLowerCase().includes(q));
    }
    if (statusFilter !== 'Tutti') {
      result = result.filter(b => b.status === statusFilter);
    }
    if (doctorFilter !== 'Tutti' && role !== 'medico') {
      result = result.filter(b => b.doctor === doctorFilter);
    }
    return result;
  }, [dashboardBookings, dateFilter, searchQuery, statusFilter, doctorFilter, role]);
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const paginatedBookings = filteredBookings.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Compatible machines for selected booking
  const compatibleMachines = useMemo(() => {
    if (!machineModalBooking) return [];
    const category = machineModalBooking.examCategory;
    const compatibleNames = machineCompatibility[category] || [];
    return allMachines.filter(m => compatibleNames.includes(m.name));
  }, [machineModalBooking]);
  // Handlers
  const handleStartDiagnostic = (booking: Booking) => {
    setMachineModalBooking(booking);
    setSelectedMachineId(null);
  };
  const confirmStartDiagnostic = () => {
    if (!machineModalBooking || !selectedMachineId) return;
    setBookings(prev => prev.map(b => b.id === machineModalBooking.id ? {
      ...b,
      status: 'In corso'
    } : b));
    setMachineModalBooking(null);
    setToastMessage({
      type: 'success',
      text: `Iter diagnostico avviato per ${machineModalBooking.patient}.`
    });
    setTimeout(() => setToastMessage(null), 4000);
  };
  const handleCompleteExam = (booking: Booking) => {
    setCompleteExamModal(booking);
  };
  const confirmCompleteExam = () => {
    if (!completeExamModal) return;
    setBookings(prev => prev.map(b => b.id === completeExamModal.id ? {
      ...b,
      status: 'Da refertare'
    } : b));
    setCompleteExamModal(null);
    setToastMessage({
      type: 'success',
      text: `Esame concluso per ${completeExamModal.patient}. Pronto per la refertazione.`
    });
    setTimeout(() => setToastMessage(null), 4000);
  };
  const handleResend = (booking: Booking) => {
    setResendModalBooking(booking);
  };
  const confirmResend = () => {
    const isSuccess = Math.random() > 0.2;
    setResendModalBooking(null);
    setToastMessage(isSuccess ? {
      type: 'success',
      text: 'Referto inviato correttamente al paziente.'
    } : {
      type: 'error',
      text: "Errore durante l'invio del referto. Riprovare."
    });
    setTimeout(() => setToastMessage(null), 4000);
  };
  // Action renderer based on date context
  const renderActions = (item: Booking) => {
    const cat = getDateCategory(item.date);
    // --- Today's bookings: full workflow ---
    if (cat === 'today') {
      if (item.status === 'In attesa') {
        return <div className="flex items-center gap-1" data-id="element-262">
            {isMobile ? <button onClick={e => {
            e.stopPropagation();
            setDetailModalBooking(item);
          }} className="p-2 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors inline-flex items-center gap-1.5 text-xs font-medium min-h-[36px]" aria-label="Visualizza dettaglio" data-id="element-263">
                <EyeIcon size={15} data-id="element-264" /> Dettaglio
              </button> : <div className="relative group" data-id="element-265">
                <button onClick={e => {
              e.stopPropagation();
              setDetailModalBooking(item);
            }} className="p-1.5 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors" aria-label="Visualizza dettaglio" data-id="element-266">
                  <EyeIcon size={16} data-id="element-267" />
                </button>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-text-primary text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{
              zIndex: 9999
            }} data-id="element-268">
                  Visualizza dettaglio prenotazione
                </div>
              </div>}
            <button onClick={e => {
            e.stopPropagation();
            handleStartDiagnostic(item);
          }} className="medical-btn-primary py-1 px-2 text-xs whitespace-nowrap inline-flex items-center gap-1 min-h-[36px] md:min-h-0 md:py-1" data-id="element-269">
              <PlayIcon size={13} data-id="element-270" /> Avvia iter
            </button>
          </div>;
      }
      if (item.status === 'In corso') {
        return <div className="flex items-center gap-1" data-id="element-271">
            {isMobile ? <button onClick={e => {
            e.stopPropagation();
            setDetailModalBooking(item);
          }} className="p-2 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors inline-flex items-center gap-1.5 text-xs font-medium min-h-[36px]" aria-label="Visualizza dettaglio" data-id="element-272">
                <EyeIcon size={15} data-id="element-273" /> Dettaglio
              </button> : <div className="relative group" data-id="element-274">
                <button onClick={e => {
              e.stopPropagation();
              setDetailModalBooking(item);
            }} className="p-1.5 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors" aria-label="Visualizza dettaglio" data-id="element-275">
                  <EyeIcon size={16} data-id="element-276" />
                </button>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-text-primary text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{
              zIndex: 9999
            }} data-id="element-277">
                  Visualizza dettaglio prenotazione
                </div>
              </div>}
            <button onClick={e => {
            e.stopPropagation();
            handleCompleteExam(item);
          }} className="medical-btn-primary py-1 px-2 text-xs whitespace-nowrap inline-flex items-center gap-1 bg-medical-orange hover:bg-medical-orange/90 border-medical-orange min-h-[36px] md:min-h-0 md:py-1" data-id="element-278">
              <StopCircleIcon size={13} data-id="element-279" /> Concludi Iter
            </button>
          </div>;
      }
      if (item.status === 'Da refertare') {
        return <div className="flex items-center gap-1" data-id="element-280">
            {isMobile ? <button onClick={e => {
            e.stopPropagation();
            setDetailModalBooking(item);
          }} className="p-2 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors inline-flex items-center gap-1.5 text-xs font-medium min-h-[36px]" aria-label="Visualizza dettaglio" data-id="element-281">
                <EyeIcon size={15} data-id="element-282" /> Dettaglio
              </button> : <div className="relative group" data-id="element-283">
                <button onClick={e => {
              e.stopPropagation();
              setDetailModalBooking(item);
            }} className="p-1.5 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors" aria-label="Visualizza dettaglio" data-id="element-284">
                  <EyeIcon size={16} data-id="element-285" />
                </button>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-text-primary text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{
              zIndex: 9999
            }} data-id="element-286">
                  Visualizza dettaglio prenotazione
                </div>
              </div>}
            <button onClick={e => {
            e.stopPropagation();
            navigate('/exams');
          }} className="medical-btn-primary py-1 px-2 text-xs whitespace-nowrap inline-flex items-center gap-1 min-h-[36px] md:min-h-0 md:py-1" data-id="element-287">
              <ArrowRightIcon size={13} data-id="element-288" /> Vai a Referti
            </button>
          </div>;
      }
    }
    // --- Past bookings ---
    if (cat === 'past') {
      return <div className="flex items-center gap-1" data-id="element-289">
          {isMobile ? <button onClick={e => {
          e.stopPropagation();
          setDetailModalBooking(item);
        }} className="p-2 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors inline-flex items-center gap-1.5 text-xs font-medium min-h-[36px]" aria-label="Visualizza dettaglio" data-id="element-290">
              <EyeIcon size={15} data-id="element-291" /> Dettaglio
            </button> : <div className="relative group" data-id="element-292">
              <button onClick={e => {
            e.stopPropagation();
            setDetailModalBooking(item);
          }} className="p-1.5 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors" aria-label="Visualizza dettaglio" data-id="element-293">
                <EyeIcon size={16} data-id="element-294" />
              </button>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-text-primary text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{
            zIndex: 9999
          }} data-id="element-295">
                Visualizza dettaglio prenotazione
              </div>
            </div>}
          {(item.status === 'Refertato' || item.status === 'Inviato') && <>
              {isMobile ? <button onClick={e => {
            e.stopPropagation();
            handleResend(item);
          }} className="p-2 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors inline-flex items-center gap-1.5 text-xs font-medium min-h-[36px]" aria-label="Reinvia referto" data-id="element-296">
                  <SendIcon size={15} data-id="element-297" /> Reinvia
                </button> : <div className="relative group" data-id="element-298">
                  <button onClick={e => {
              e.stopPropagation();
              handleResend(item);
            }} className="p-1.5 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors" aria-label="Reinvia referto" data-id="element-299">
                    <SendIcon size={16} data-id="element-300" />
                  </button>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-text-primary text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{
              zIndex: 9999
            }} data-id="element-301">
                    Reinvia il referto al paziente
                  </div>
                </div>}
            </>}
        </div>;
    }
    // --- Future bookings: view only ---
    return isMobile ? <button onClick={e => {
      e.stopPropagation();
      setDetailModalBooking(item);
    }} className="p-2 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors inline-flex items-center gap-1.5 text-xs font-medium min-h-[36px]" aria-label="Visualizza dettaglio" data-id="element-302">
        <EyeIcon size={15} data-id="element-303" /> Dettaglio
      </button> : <div className="relative group" data-id="element-304">
        <button onClick={e => {
        e.stopPropagation();
        setDetailModalBooking(item);
      }} className="p-1.5 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors" aria-label="Visualizza dettaglio" data-id="element-305">
          <EyeIcon size={16} data-id="element-306" />
        </button>
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-text-primary text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{
        zIndex: 9999
      }} data-id="element-307">
          Visualizza dettaglio prenotazione
        </div>
      </div>;
  };
  return <div className="space-y-6 w-full min-w-0" data-id="element-308">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3" data-id="element-309">
        <div data-id="element-310">
          <h1 className="text-2xl md:text-page-title mb-1 md:mb-2 text-text-primary font-bold" data-id="element-311">
            Prenotazioni
          </h1>
          <p className="text-sm md:text-body text-text-secondary" data-id="element-312">
            Visualizzazione e gestione delle prenotazioni
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 self-start sm:self-center" data-id="element-313">
          <span className="text-xs text-text-tertiary" data-id="element-314">
            Ultimo import: 11/03/2026 07:45
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-status-success-bg text-status-success" data-id="element-315">
            Completato
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center bg-white p-4 rounded-medical-lg shadow-sm border border-clinical-gray-100" data-id="element-316">
        {/* Search */}
        <div className="relative w-full md:w-64" data-id="element-317">
          <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-318" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Cerca per paziente, medico, ID..." className="medical-input pl-9 text-sm h-9 w-full" data-id="element-319" />
        </div>
        {/* Date */}
        <input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} className="medical-input text-sm h-9 w-full md:w-auto" data-id="element-320" />
        {/* Status */}
        <div className="relative w-full md:w-auto" data-id="element-321">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="medical-input text-sm h-9 pr-8 appearance-none cursor-pointer w-full md:w-auto" data-id="element-322">
            <option value="Tutti" data-id="element-323">Stato: Tutti</option>
            {statusOptions.map(s => <option key={s} value={s} data-id="element-324">
                {s}
              </option>)}
          </select>
          <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-325" />
        </div>

        {/* Doctor */}
        {role !== 'medico' && <div className="relative w-full md:w-auto" data-id="element-326">
            <select value={doctorFilter} onChange={e => setDoctorFilter(e.target.value)} className="medical-input text-sm h-9 pr-8 appearance-none cursor-pointer w-full md:w-auto" data-id="element-327">
              <option value="Tutti" data-id="element-328">Medico: Tutti</option>
              {uniqueDoctors.map(d => <option key={d} value={d} data-id="element-329">
                  {d}
                </option>)}
            </select>
            <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-330" />
          </div>}
      </div>

      {/* Table */}
      {!isMobile ? <div className="w-full max-w-full overflow-x-auto overflow-y-visible bg-white rounded-medical-lg shadow-card border border-clinical-gray-100" data-id="element-331">
          <table className="w-full text-left border-collapse" style={{
        tableLayout: 'fixed',
        minWidth: '900px'
      }} data-id="element-332">
            <colgroup data-id="element-333">
              {role !== 'medico' ? <>
                  <col style={{
              width: '12%'
            }} data-id="element-334" />
                  <col style={{
              width: '16%'
            }} data-id="element-335" />
                  <col style={{
              width: '22%'
            }} data-id="element-336" />
                  <col style={{
              width: '14%'
            }} data-id="element-337" />
                  <col style={{
              width: '8%'
            }} data-id="element-338" />
                  <col style={{
              width: '12%'
            }} data-id="element-339" />
                  <col style={{
              width: '16%'
            }} data-id="element-340" />
                </> : <>
                  <col style={{
              width: '14%'
            }} data-id="element-341" />
                  <col style={{
              width: '18%'
            }} data-id="element-342" />
                  <col style={{
              width: '28%'
            }} data-id="element-343" />
                  <col style={{
              width: '10%'
            }} data-id="element-344" />
                  <col style={{
              width: '14%'
            }} data-id="element-345" />
                  <col style={{
              width: '16%'
            }} data-id="element-346" />
                </>}
            </colgroup>
            <thead data-id="element-347">
              <tr className="bg-clinical-gray border-b border-clinical-gray-200" data-id="element-348">
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-349">
                  ID
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-350">
                  Paziente
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-351">
                  Tipo Esame
                </th>
                {role !== 'medico' && <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-352">
                    Medico
                  </th>}
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-353">
                  Ora
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-354">
                  Stato
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-355">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody data-id="element-356">
              {paginatedBookings.length === 0 ? <tr data-id="element-357">
                  <td colSpan={7} className="px-4 py-8 text-center text-text-secondary text-body" data-id="element-358">
                    Nessuna prenotazione trovata per questa data.
                  </td>
                </tr> : paginatedBookings.map(item => <tr key={item.id} className="border-b border-clinical-gray-100 last:border-0 hover:bg-clinical-gray/30 transition-colors" data-id="element-359">
                    <td className="px-4 py-3 text-body text-text-primary overflow-hidden" data-id="element-360">
                      <TruncatedCell text={item.id} data-id="element-361" />
                    </td>
                    <td className="px-4 py-3 text-body text-text-primary overflow-hidden" data-id="element-362">
                      <TruncatedCell text={item.patient} data-id="element-363" />
                    </td>
                    <td className="px-4 py-3 text-body text-text-primary overflow-hidden" data-id="element-364">
                      <TruncatedCell text={item.type} data-id="element-365" />
                    </td>
                    {role !== 'medico' && <td className="px-4 py-3 text-body text-text-primary overflow-hidden" data-id="element-366">
                        <TruncatedCell text={item.doctor} data-id="element-367" />
                      </td>}
                    <td className="px-4 py-3 text-body text-text-secondary" data-id="element-368">
                      {item.time}
                    </td>
                    <td className="px-4 py-3" data-id="element-369">
                      <StatusBadge status={item.status} data-id="element-370" />
                    </td>
                    <td className="px-4 py-3 pl-[0px] pr-[0px] ml-[0px] mr-[0px]" data-id="element-371">
                      {renderActions(item)}
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div> : <div className="space-y-3" data-id="element-372">
          {paginatedBookings.length === 0 ? <div className="p-6 text-center text-text-secondary text-body bg-white rounded-medical-lg shadow-card border border-clinical-gray-100" data-id="element-373">
              Nessuna prenotazione trovata per questa data.
            </div> : paginatedBookings.map(item => <div key={item.id} className="bg-white rounded-medical-lg shadow-card border border-clinical-gray-100 p-4 space-y-3" data-id="element-374">
                <div className="flex justify-between items-start" data-id="element-375">
                  <div className="min-w-0 mr-3" data-id="element-376">
                    <span className="text-xs text-text-tertiary block mb-0.5" data-id="element-377">
                      {item.id}
                    </span>
                    <span className="text-sm font-medium text-text-primary truncate block" data-id="element-378">
                      {item.patient}
                    </span>
                  </div>
                  <StatusBadge status={item.status} data-id="element-379" />
                </div>
                <div className="grid grid-cols-2 gap-2" data-id="element-380">
                  <div className="min-w-0" data-id="element-381">
                    <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-0.5" data-id="element-382">
                      Esame
                    </span>
                    <span className="text-sm text-text-primary truncate block" data-id="element-383">
                      {item.type}
                    </span>
                  </div>
                  {role !== 'medico' && <div className="min-w-0" data-id="element-384">
                      <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-0.5" data-id="element-385">
                        Medico
                      </span>
                      <span className="text-sm text-text-primary truncate block" data-id="element-386">
                        {item.doctor}
                      </span>
                    </div>}
                </div>
                <div className="pt-3 border-t border-clinical-gray-100 flex justify-between items-center" data-id="element-387">
                  <span className="text-xs text-text-tertiary" data-id="element-388">
                    {item.time}
                  </span>
                  {renderActions(item)}
                </div>
              </div>)}
        </div>}

      {/* Pagination */}
      {totalPages > 1 && <div className="flex items-center justify-between px-1" data-id="element-389">
          <span className="text-sm text-text-secondary" data-id="element-390">
            {filteredBookings.length} risultati — Pagina {currentPage} di{' '}
            {totalPages}
          </span>
          <div className="flex items-center gap-2" data-id="element-391">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="w-8 h-8 flex items-center justify-center rounded border border-clinical-gray-200 text-text-secondary hover:bg-clinical-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-392">
              <ChevronDownIcon size={16} className="rotate-90" data-id="element-393" />
            </button>
            <div className="flex items-center gap-1" data-id="element-394">
              {Array.from({
            length: totalPages
          }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium transition-colors ${currentPage === page ? 'bg-medical-green text-white border border-medical-green' : 'border border-clinical-gray-200 text-text-secondary hover:bg-clinical-gray-50'}`} data-id="element-395">
                  {page}
                </button>)}
            </div>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="w-8 h-8 flex items-center justify-center rounded border border-clinical-gray-200 text-text-secondary hover:bg-clinical-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-396">
              <ChevronDownIcon size={16} className="-rotate-90" data-id="element-397" />
            </button>
          </div>
        </div>}

      {/* Machine Selection Modal */}
      <ModalDialog isOpen={!!machineModalBooking} onClose={() => setMachineModalBooking(null)} title="Seleziona macchinario diagnostico" footer={<>
            <button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setMachineModalBooking(null)} data-id="element-399">
              Annulla
            </button>
            <button className="medical-btn-primary w-full sm:w-auto" disabled={!selectedMachineId} onClick={confirmStartDiagnostic} data-id="element-400">
              <PlayIcon size={16} className="mr-1.5" data-id="element-401" /> Avvia iter esame
            </button>
          </>} data-id="element-398">
        {machineModalBooking && <div className="space-y-4" data-id="element-402">
            <p className="text-sm text-text-secondary" data-id="element-403">
              Seleziona un macchinario compatibile per l'esame{' '}
              <span className="font-semibold text-text-primary" data-id="element-404">
                {machineModalBooking.type}
              </span>{' '}
              di{' '}
              <span className="font-semibold text-text-primary" data-id="element-405">
                {machineModalBooking.patient}
              </span>
              .
            </p>
            <div className="space-y-3" data-id="element-406">
              {compatibleMachines.length === 0 ? <div className="p-4 text-center text-text-secondary text-sm bg-clinical-gray rounded-medical" data-id="element-407">
                  Nessun macchinario compatibile trovato.
                </div> : compatibleMachines.map(machine => {
            const isDisabled = machine.status !== 'Connesso';
            const isSelected = selectedMachineId === machine.id;
            return <label key={machine.id} className={`
                        flex items-start gap-3 p-4 rounded-medical border cursor-pointer transition-all
                        ${isSelected ? 'border-medical-green bg-medical-green-50 ring-1 ring-medical-green' : 'border-clinical-gray-200 hover:border-medical-green/50 hover:bg-clinical-gray-50'}
                        ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                      `} data-id="element-408">
                      <input type="radio" name="machine" value={machine.id} checked={isSelected} onChange={() => setSelectedMachineId(machine.id)} disabled={isDisabled} className="w-4 h-4 mt-1 text-medical-green focus:ring-medical-green border-clinical-gray-300 shrink-0" data-id="element-409" />
                      <div className="flex-1 min-w-0" data-id="element-410">
                        <div className="flex items-center justify-between gap-2 mb-1" data-id="element-411">
                          <p className="text-sm font-medium text-text-primary truncate" data-id="element-412">
                            {machine.name}
                          </p>
                          <MachineStatusIndicator status={machine.status} className="shrink-0" data-id="element-413" />
                        </div>
                        <p className="text-xs text-text-secondary mb-2" data-id="element-414">
                          {machine.type}
                        </p>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs text-text-tertiary" data-id="element-415">
                          <span className="flex items-center gap-1" data-id="element-416">
                            <ClockIcon size={12} data-id="element-417" /> Ultima attività:{' '}
                            {machine.lastActivity}
                          </span>
                          <span className="flex items-center gap-1" data-id="element-418">
                            <CpuIcon size={12} data-id="element-419" /> Tempo stimato:{' '}
                            {machine.estimatedTime}
                          </span>
                        </div>
                      </div>
                    </label>;
          })}
            </div>
          </div>}
      </ModalDialog>

      {/* Detail Modal */}
      <ModalDialog isOpen={!!detailModalBooking} onClose={() => setDetailModalBooking(null)} title="Dettaglio Prenotazione" maxWidth="max-w-2xl" footer={<button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setDetailModalBooking(null)} data-id="element-421">
            Chiudi
          </button>} data-id="element-420">
        {detailModalBooking && <div className="space-y-4" data-id="element-422">
            <div className="grid grid-cols-2 gap-4" data-id="element-423">
              <div data-id="element-424">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-425">
                  ID Prenotazione
                </span>
                <span className="text-sm font-medium text-text-primary" data-id="element-426">
                  {detailModalBooking.id}
                </span>
              </div>
              <div data-id="element-427">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-428">
                  Stato
                </span>
                <StatusBadge status={detailModalBooking.status} data-id="element-429" />
              </div>
              <div data-id="element-430">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-431">
                  Paziente
                </span>
                <span className="text-sm font-medium text-text-primary" data-id="element-432">
                  {detailModalBooking.patient}
                </span>
              </div>
              <div data-id="element-433">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-434">
                  Email
                </span>
                <div className="flex items-center gap-1.5 text-sm text-text-primary" data-id="element-435">
                  <MailIcon size={14} className="text-text-tertiary" data-id="element-436" />
                  {detailModalBooking.email}
                </div>
              </div>
              <div data-id="element-437">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-438">
                  Tipo Esame
                </span>
                <span className="text-sm font-medium text-text-primary" data-id="element-439">
                  {detailModalBooking.type}
                </span>
              </div>
              <div data-id="element-440">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-441">
                  Medico
                </span>
                <span className="text-sm font-medium text-text-primary" data-id="element-442">
                  {detailModalBooking.doctor}
                </span>
              </div>
              <div data-id="element-443">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-444">
                  Data
                </span>
                <span className="text-sm font-medium text-text-primary" data-id="element-445">
                  {detailModalBooking.date}
                </span>
              </div>
              <div data-id="element-446">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-447">
                  Ora
                </span>
                <span className="text-sm font-medium text-text-primary" data-id="element-448">
                  {detailModalBooking.time}
                </span>
              </div>
            </div>
          </div>}
      </ModalDialog>

      {/* Resend Modal */}
      <ModalDialog isOpen={!!resendModalBooking} onClose={() => setResendModalBooking(null)} title="Reinvia Referto" footer={<>
            <button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setResendModalBooking(null)} data-id="element-450">
              Annulla
            </button>
            <button className="medical-btn-primary w-full sm:w-auto" onClick={confirmResend} data-id="element-451">
              <SendIcon size={16} className="mr-1.5" data-id="element-452" /> Reinvia referto
            </button>
          </>} data-id="element-449">
        {resendModalBooking && <div className="space-y-4" data-id="element-453">
            <div className="bg-status-info-bg border border-status-info/20 rounded-medical p-4 flex items-start gap-3" data-id="element-454">
              <MailIcon className="text-status-info shrink-0 mt-0.5" size={20} data-id="element-455" />
              <div data-id="element-456">
                <p className="text-sm text-text-primary font-medium mb-1" data-id="element-457">
                  Stai per reinviare il referto al paziente.
                </p>
                <p className="text-sm text-text-secondary" data-id="element-458">
                  Un'email verrà inviata all'indirizzo registrato.
                </p>
              </div>
            </div>
            <div className="bg-white border border-clinical-gray-200 rounded-medical-lg p-4 space-y-3" data-id="element-459">
              <div className="flex justify-between items-center border-b border-clinical-gray-100 pb-3" data-id="element-460">
                <span className="text-sm text-text-secondary" data-id="element-461">Paziente</span>
                <span className="text-sm font-medium text-text-primary" data-id="element-462">
                  {resendModalBooking.patient}
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-clinical-gray-100 pb-3" data-id="element-463">
                <span className="text-sm text-text-secondary" data-id="element-464">Email</span>
                <span className="text-sm font-medium text-text-primary" data-id="element-465">
                  {resendModalBooking.email}
                </span>
              </div>
              <div className="flex justify-between items-center pt-1" data-id="element-466">
                <span className="text-sm text-text-secondary" data-id="element-467">Esame</span>
                <span className="text-sm font-medium text-text-primary" data-id="element-468">
                  {resendModalBooking.type}
                </span>
              </div>
            </div>
          </div>}
      </ModalDialog>

      {/* Complete Exam Confirmation Modal */}
      <ModalDialog isOpen={!!completeExamModal} onClose={() => setCompleteExamModal(null)} title="Confermare conclusione esame?" footer={<>
            <button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setCompleteExamModal(null)} data-id="element-470">
              Annulla
            </button>
            <button className="medical-btn-primary w-full sm:w-auto" onClick={confirmCompleteExam} data-id="element-471">
              Conferma
            </button>
          </>} data-id="element-469">
        {completeExamModal && <div className="space-y-4" data-id="element-472">
            <p className="text-sm text-text-secondary" data-id="element-473">
              Confermando, l'esame verrà chiuso e passerà allo stato{' '}
              <span className="font-semibold text-text-primary" data-id="element-474">
                'Da Refertare'
              </span>
              .
            </p>
            <div className="bg-white border border-clinical-gray-200 rounded-medical-lg p-4 space-y-3" data-id="element-475">
              <div className="flex justify-between items-center border-b border-clinical-gray-100 pb-3" data-id="element-476">
                <span className="text-sm text-text-secondary" data-id="element-477">Paziente</span>
                <span className="text-sm font-medium text-text-primary" data-id="element-478">
                  {completeExamModal.patient}
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-clinical-gray-100 pb-3" data-id="element-479">
                <span className="text-sm text-text-secondary" data-id="element-480">Esame</span>
                <span className="text-sm font-medium text-text-primary" data-id="element-481">
                  {completeExamModal.type}
                </span>
              </div>
              <div className="flex justify-between items-center pt-1" data-id="element-482">
                <span className="text-sm text-text-secondary" data-id="element-483">Medico</span>
                <span className="text-sm font-medium text-text-primary" data-id="element-484">
                  {completeExamModal.doctor}
                </span>
              </div>
            </div>
          </div>}
      </ModalDialog>

      {/* Toast */}
      <AnimatePresence data-id="element-485">
        {toastMessage && <motion.div initial={{
        opacity: 0,
        y: 50,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} className={`fixed bottom-4 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-6 sm:bottom-6 z-50 flex items-center gap-3 px-4 py-3 rounded-medical-lg shadow-modal border w-[calc(100%-2rem)] max-w-[calc(100%-2rem)] sm:w-auto sm:max-w-md ${toastMessage.type === 'success' ? 'bg-white border-status-success/20' : 'bg-white border-status-error/20'}`} data-id="element-486">
            {toastMessage.type === 'success' ? <CheckCircleIcon className="text-status-success shrink-0" size={20} data-id="element-487" /> : <XCircleIcon className="text-status-error shrink-0" size={20} data-id="element-488" />}
            <span className="text-sm font-medium text-text-primary" data-id="element-489">
              {toastMessage.text}
            </span>
            <button onClick={() => setToastMessage(null)} className="ml-2 p-1 text-text-tertiary hover:text-text-primary rounded-full transition-colors" data-id="element-490">
              <XIcon size={16} data-id="element-491" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}